import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { linkQrText, type MyPatientLink, type PublicConsent } from '@curentia/shared';
import { ErrorText, GroupedCode, LockButton, Page } from '../components';
import { CONSENT_STATUS_LABELS, HI_TYPE_LABELS, formatDate, formatDateTime, readingsOf, requesterOf, sourcesOf, timelineOf } from '../lib/health';
import { PRACTICE_TYPE_LABELS } from '../lib/practices';
import { useLoad } from '../lib/useLoad';
import { QrCode } from '../qr';
import { useAction, useDevice, useSnapshot } from '../state';
import { ReadingsPanel, Timeline } from './Health';
import { PrescriptionList } from './Prescriptions';

const groupCode = (code: string) => code.replace(/^(.{5})-?(.{5})$/, '$1-$2');

function LinkRequest({ link, onDone }: { link: MyPatientLink; onDone: () => void }) {
  const device = useDevice();
  const { busy, error, run } = useAction();
  return (
    <li className="member" aria-label={`${link.practice.name} asks to link`}>
      <strong>{link.practice.name} wants to link your account</strong>
      <span className="muted">
        {PRACTICE_TYPE_LABELS[link.practice.type]}, {link.practice.city}. Asked by {link.requestedBy.name}.
      </span>
      <span className="muted">Linking lets them ask for your history. It shares nothing by itself.</span>
      <ErrorText error={error} />
      <div className="actions">
        <button
          type="button"
          className="button small"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await device.api.approveLink(link.id);
              onDone();
            })
          }
        >
          Link {link.practice.name}
        </button>
        <button
          type="button"
          className="button secondary small"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await device.api.declineLink(link.id);
              onDone();
            })
          }
        >
          Decline
        </button>
      </div>
    </li>
  );
}

/** On the patient's home screen: link requests and requests for history waiting for an answer. */
export function PatientRequests() {
  const device = useDevice();
  const { linksVersion, consentsVersion } = useSnapshot();
  const links = useLoad(() => device.api.myLinks(), [device, linksVersion]);
  const consents = useLoad(() => device.api.consents({ status: 'requested' }), [device, consentsVersion]);
  const asked = (links.data?.links ?? []).filter((l) => l.status === 'requested');
  const requests = consents.data?.consents ?? [];
  const loaded = links.data !== null && consents.data !== null;
  return (
    <section className="card" aria-labelledby="requests-heading">
      <h2 id="requests-heading">Waiting for your answer</h2>
      <ErrorText error={links.error ?? consents.error} />
      {!loaded && !links.error && !consents.error ? <p className="muted">Loading…</p> : null}
      {loaded && asked.length + requests.length === 0 ? <p className="muted">Nothing is waiting.</p> : null}
      {asked.length + requests.length > 0 ? (
        <ul className="list" aria-label="Requests">
          {asked.map((link) => (
            <LinkRequest key={link.id} link={link} onDone={links.reload} />
          ))}
          {requests.map((consent) => (
            <li key={consent.id}>
              <Link className="list-link" to={`/consents/${consent.id}`}>
                <strong>{requesterOf(consent)} asks to see your records</strong>
                <span className="muted">
                  Asked by {consent.requestedByName} on {formatDate(consent.createdAt)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** On the patient's home screen: the newest prescriptions their doctors sent. */
export function RecentPrescriptions() {
  const device = useDevice();
  const { recordsVersion } = useSnapshot();
  const { data } = useLoad(async () => (await device.personal().documents()).filter((r) => r.hiType === 'Prescription').slice(0, 3), [device, recordsVersion]);
  if (!data || data.length === 0) return null;
  return (
    <section className="card" aria-labelledby="recent-prescriptions-heading">
      <h2 id="recent-prescriptions-heading">Latest prescriptions</h2>
      <PrescriptionList records={data} />
      <div className="links">
        <Link to="/prescriptions">All your prescriptions</Link>
      </div>
    </section>
  );
}

/** On the patient's home screen: their code, records, readings and sharing. */
export function PatientShortcuts() {
  return (
    <section className="card" aria-labelledby="health-heading">
      <h2 id="health-heading">Your health</h2>
      <div className="stack">
        <Link to="/my-code" className="button">
          Show my code to a clinic
        </Link>
        <div className="pair">
          <Link to="/prescriptions" className="button secondary">
            Prescriptions
          </Link>
          <Link to="/readings" className="button secondary">
            Readings
          </Link>
        </div>
        <Link to="/records" className="button secondary">
          My records
        </Link>
        <Link to="/sharing" className="button secondary">
          Who can see my records
        </Link>
      </div>
    </section>
  );
}

/** The patient's one-time link code, as a QR code and as text, for the front desk to scan or type. */
export function MyCode() {
  const device = useDevice();
  const { deviceId } = useSnapshot();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const { busy, error, run } = useAction();
  const make = useCallback(() => run(async () => setCode(await device.api.createLinkCode())), [device, run]);
  useEffect(() => {
    void make();
  }, [make]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(timer);
  }, []);
  const expired = code !== null && Date.parse(code.expiresAt) <= now;
  return (
    <Page actions={<LockButton />}>
      <h1>Your link code</h1>
      <p>Show this to the doctor or the front desk. It links your Curentia account to their practice, so they can ask you for your history. You decide what they see.</p>
      <ErrorText error={error} />
      {code && deviceId && !expired ? (
        <section className="card qr-card" aria-label="Your code">
          <QrCode text={linkQrText(code.code, deviceId)} label="Your link code as a QR code" />
          <GroupedCode code={groupCode(code.code)} label="Your link code" testId="link-code" />
          <p className="muted">It works once, until {new Date(code.expiresAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}.</p>
        </section>
      ) : null}
      {expired ? <p className="note">This code has expired. Make a new one.</p> : null}
      {!code && !error ? <p className="muted">Making a code…</p> : null}
      <button type="button" className="button secondary" disabled={busy} onClick={() => void make()}>
        Make a new code
      </button>
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

/** The records the patient's doctors sent to this phone, newest first. */
export function MyRecords() {
  const device = useDevice();
  const { recordsVersion } = useSnapshot();
  const { data, error } = useLoad(() => device.personal().documents(), [device, recordsVersion]);
  return (
    <Page actions={<LockButton />}>
      <h1>Your records</h1>
      <p className="muted">Your doctors send them to this phone, where they are kept encrypted. A backup keeps them safe if you lose it.</p>
      <ErrorText error={error} />
      <section className="card" aria-labelledby="timeline-heading">
        <h2 id="timeline-heading">Timeline</h2>
        {data ? (
          <Timeline
            items={timelineOf(data, 'you')}
            empty="Nothing yet. Prescriptions and readings from your doctors appear here."
            linkFor={(item) => (item.hiType === 'Prescription' ? `/prescriptions/${item.id}` : null)}
          />
        ) : error ? null : (
          <p className="muted">Loading…</p>
        )}
      </section>
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

/** The readings the patient's doctors took, with a chart. Patients do not enter readings themselves. */
export function MyReadings() {
  const device = useDevice();
  const { recordsVersion } = useSnapshot();
  const { data, error } = useLoad(() => device.personal().documents(), [device, recordsVersion]);
  return (
    <Page actions={<LockButton />}>
      <h1>Your readings</h1>
      <p className="muted">Blood pressure, sugar, weight, pulse, SpO2 and temperature, as your doctors record them. Each one shows where it was taken.</p>
      <ErrorText error={error} />
      {data ? <ReadingsPanel points={readingsOf(data, 'you')} here="you" /> : error ? null : <p className="muted">Loading…</p>}
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

function ConsentSummary({ consent }: { consent: PublicConsent }) {
  const requester = consent.practices.find((p) => p.id === consent.requesterPracticeId);
  return (
    <dl>
      <dt>Asked by</dt>
      <dd>{consent.requestedByName}</dd>
      <dt>Practice</dt>
      <dd>{requester ? `${requester.name}, ${requester.city}` : 'Unknown'}</dd>
      <dt>Records</dt>
      <dd>
        <ul className="plain">
          {consent.hiTypes.map((type) => (
            <li key={type}>{HI_TYPE_LABELS[type]}</li>
          ))}
        </ul>
      </dd>
      <dt>Dated</dt>
      <dd>
        {formatDate(consent.from)} to {formatDate(consent.to)}
      </dd>
      <dt>Until</dt>
      <dd>{formatDate(consent.expiresAt)}</dd>
      <dt>Status</dt>
      <dd>
        <span className={`badge ${consent.status}`} data-testid="consent-status">
          {CONSENT_STATUS_LABELS[consent.status]}
        </span>
      </dd>
    </dl>
  );
}

/** A request for history: what it covers, and approve, deny or withdraw. */
export function ConsentReview() {
  const { id = '' } = useParams();
  const device = useDevice();
  const { consentsVersion, linksVersion } = useSnapshot();
  const { data, error } = useLoad(() => device.api.consent(id), [device, id, consentsVersion]);
  const links = useLoad(() => device.api.myLinks(), [device, linksVersion]);
  const [sources, setSources] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<string | null>(null);
  const action = useAction();
  const consent = data?.consent;
  if (!consent) {
    return (
      <Page actions={<LockButton />}>
        <h1>Request for your records</h1>
        <ErrorText error={error} />
        {error ? null : <p className="muted">Loading…</p>}
        <div className="links">
          <Link to="/home">Back</Link>
        </div>
      </Page>
    );
  }
  const others = (links.data?.links ?? []).filter((l) => l.status === 'active' && l.practice.id !== consent.requesterPracticeId && l.practice.type !== 'pharmacy');
  const toggle = (practiceId: string) => setSources((now) => (now.includes(practiceId) ? now.filter((p) => p !== practiceId) : [...now, practiceId]));
  const requester = requesterOf(consent);
  return (
    <Page actions={<LockButton />}>
      <h1>{requester} asks to see your records</h1>
      <section className="card" aria-label="What they ask for">
        <ConsentSummary consent={consent} />
      </section>
      {outcome ? (
        <p className="note" role="status">
          {outcome}
        </p>
      ) : null}
      <ErrorText error={action.error} />
      {consent.status === 'requested' ? (
        <section className="card" aria-labelledby="answer-heading">
          <h2 id="answer-heading">Your answer</h2>
          {others.length > 0 ? (
            <p>If you approve, the practices you tick send {requester} the records they made that match. Nothing else is shared.</p>
          ) : (
            <p className="note">You are not linked with any other practice that could send {requester} your records, so there is nothing to approve yet. Show your code at a clinic to link it.</p>
          )}
          {others.length > 0 ? (
            <fieldset>
              <legend>Let these practices send their records</legend>
              <div className="choices compact">
                {others.map((link) => (
                  <label key={link.id} className="check">
                    <input type="checkbox" checked={sources.includes(link.practice.id)} onChange={() => toggle(link.practice.id)} />
                    <span>
                      {link.practice.name}, {link.practice.city}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}
          {others.length > 0 ? <p className="muted">Approving signs your answer with this phone’s key, so every practice can check it came from you.</p> : null}
          <div className="stack">
            {others.length > 0 ? (
              <button
                type="button"
                className="button"
                disabled={action.busy}
                onClick={() => {
                  if (sources.length === 0) return action.setError('Tick at least one practice to send its records, or deny the request.');
                  void action.run(async () => {
                    const { sent } = await device.approveConsent(consent, sources);
                    const names = others.filter((l) => sources.includes(l.practice.id)).map((l) => l.practice.name);
                    setOutcome(`Approved.${sent > 0 ? ` Your phone sent ${sent} ${sent === 1 ? 'record' : 'records'}.` : ''} ${names.join(' and ')} will send theirs.`);
                  });
                }}
              >
                {action.busy ? 'Approving…' : 'Approve and share'}
              </button>
            ) : null}
            <button
              type="button"
              className="button secondary"
              disabled={action.busy}
              onClick={() =>
                void action.run(async () => {
                  await device.denyConsent(consent.id);
                  setOutcome('Denied. Nothing was shared.');
                })
              }
            >
              Deny
            </button>
          </div>
        </section>
      ) : null}
      {consent.status === 'approved' ? (
        <section className="card" aria-labelledby="approved-heading">
          <h2 id="approved-heading">You approved this</h2>
          <p>
            On {formatDateTime(consent.decidedAt ?? consent.createdAt)}.{' '}
            {sourcesOf(consent).length > 0 ? `${sourcesOf(consent).join(' and ')} may send their records.` : 'No practice was named to send records.'}
          </p>
          <p className="muted">Withdrawing stops anything more being sent. Records already sent stay with {requester}.</p>
          <button
            type="button"
            className="button danger"
            disabled={action.busy}
            onClick={() =>
              void action.run(async () => {
                await device.revokeConsent(consent.id);
                setOutcome('Withdrawn. Nothing more will be sent under this approval.');
              })
            }
          >
            Withdraw approval
          </button>
        </section>
      ) : null}
      <div className="links">
        <Link to="/sharing">Who can see my records</Link>
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

/** Everything the patient has approved, asked for and linked, in one place. */
export function Sharing() {
  const device = useDevice();
  const { consentsVersion, linksVersion } = useSnapshot();
  const consents = useLoad(() => device.api.consents(), [device, consentsVersion]);
  const links = useLoad(() => device.api.myLinks(), [device, linksVersion]);
  const { busy, error, run } = useAction();
  const all = consents.data?.consents ?? [];
  const approved = all.filter((c) => c.status === 'approved');
  const waiting = all.filter((c) => c.status === 'requested');
  const earlier = all.filter((c) => c.status !== 'approved' && c.status !== 'requested');
  const linked = (links.data?.links ?? []).filter((l) => l.status === 'active');
  const item = (consent: PublicConsent, extra: string) => (
    <li key={consent.id}>
      <Link className="list-link" to={`/consents/${consent.id}`}>
        <strong>{requesterOf(consent)}</strong>
        <span className="muted">{extra}</span>
      </Link>
    </li>
  );
  return (
    <Page actions={<LockButton />}>
      <h1>Who can see your records</h1>
      <ErrorText error={consents.error ?? links.error ?? error} />
      <section className="card" aria-labelledby="approved-list">
        <h2 id="approved-list">Approved</h2>
        {approved.length === 0 ? <p className="muted">{consents.data ? 'No practice can see your records now.' : 'Loading…'}</p> : null}
        {approved.length > 0 ? <ul className="list">{approved.map((c) => item(c, `Until ${formatDate(c.expiresAt)}`))}</ul> : null}
      </section>
      {waiting.length > 0 ? (
        <section className="card" aria-labelledby="waiting-list">
          <h2 id="waiting-list">Waiting for your answer</h2>
          <ul className="list">{waiting.map((c) => item(c, `Asked on ${formatDate(c.createdAt)}`))}</ul>
        </section>
      ) : null}
      <section className="card" aria-labelledby="linked-list">
        <h2 id="linked-list">Linked practices</h2>
        <p className="muted">They can ask for your history. You can name them as sources when another practice asks.</p>
        {links.data && linked.length === 0 ? <p className="muted">None yet. Show your code at a clinic to link it.</p> : null}
        {linked.length > 0 ? (
          <ul className="list" aria-label="Linked practices">
            {linked.map((link) => (
              <li key={link.id} className="member">
                <strong>{link.practice.name}</strong>
                <span className="muted">
                  {PRACTICE_TYPE_LABELS[link.practice.type]}, {link.practice.city}
                  {link.decidedAt ? ` · linked ${formatDate(link.decidedAt)}` : ''}
                </span>
                <button
                  type="button"
                  className="button secondary small"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await device.api.removeLink(link.id);
                      links.reload();
                    })
                  }
                >
                  Remove link
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      {earlier.length > 0 ? (
        <section className="card" aria-labelledby="earlier-list">
          <h2 id="earlier-list">Earlier</h2>
          <ul className="list">{earlier.map((c) => item(c, `${CONSENT_STATUS_LABELS[c.status]} · asked ${formatDate(c.createdAt)}`))}</ul>
        </section>
      ) : null}
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}
